import { Link } from "react-router-dom";
import {
  MapPin,
  Building2,
} from "lucide-react";
import { CardStatusTag, CardSaveButton } from "./CardHeaderActions";
import { cn } from "@/lib/utils";
import { fmtSalary, getPrimaryImg } from "@/utils";
import { Job } from "@/types";
import { useAuthStore } from "@/store/authStore";
import { PERMISSIONS } from "@/routes/guards";
import { useToggleSaveListing } from "@/hooks/useSavedListings";

type Props = {
  job: Job;
};

export default function JobCard({ job }: Props) {
  const { user } = useAuthStore();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const saved = isSaved(job.id);

  // Chỉ hiển thị tag trạng thái cho Headhunter/Admin. 
  // Seller, Buyer, Job Seeker không cần thấy tag trạng thái duyệt/chờ duyệt.
  const showStatusTag = !!user && PERMISSIONS.canCreateJob(user.role);

  const salaryStr = fmtSalary(job.salaryMin, job.salaryMax, job.currencyUnit);
  const location = job.province?.name || job.workingLocation || "Toàn quốc";

  return (
    <div className="relative group h-full">
      <Link
        to={`/viec-lam/${job.id}`}
        className="fnb-card block p-4 h-full animate-fade-up hover:border-orange-200 transition-all"
      >
        <div className="flex gap-4">
          {/* Left: Logo */}
          <div
            className="w-16 h-16 rounded-xl overflow-hidden border shrink-0 flex items-center justify-center bg-white"
            style={{ borderColor: "var(--border-c)" }}
          >
            {job.company?.logoUrl ? (
              <img
                src={job.company.logoUrl}
                alt={job.company.name}
                className="w-full h-full object-contain p-1"
              />
            ) : (
              <Building2 size={24} style={{ color: "var(--brand)" }} />
            )}
          </div>

          {/* Middle: Info */}
          <div className="flex-1 min-w-0">
            <div className="mb-1">
              <CardStatusTag
                show={showStatusTag || (!showStatusTag && job.approvalStatus === "approved")}
                status={showStatusTag ? job.approvalStatus : "hot"}
                label={showStatusTag ? (job.approvalStatus === "approved" ? "Đang tuyển" : job.approvalStatus === "rejected" ? "Từ chối" : "Chờ duyệt") : "HOT"}
              />
            </div>

            <h3 className="font-bold text-base leading-snug text-gray-900 group-hover:text-orange-500 transition-colors line-clamp-1 mb-0.5 mt-1 pr-6">
              {job.title}
            </h3>

            <p className="text-xs text-gray-500 truncate mb-3 pr-6">
              {job.company?.name}
            </p>

            <div className="flex flex-wrap gap-2 pr-10">
              <span className="bg-gray-100 text-gray-700 text-[11px] font-semibold px-2.5 py-1 rounded-full">
                {salaryStr}
              </span>
              <span className="bg-gray-100 text-gray-700 text-[11px] font-semibold px-2.5 py-1 rounded-full">
                {location}
              </span>
            </div>
          </div>
        </div>
      </Link>

      <CardSaveButton
        className="absolute bottom-4 right-4 z-10"
        saved={saved}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSave(job.id);
        }}
      />
    </div>
  );
}
